// The shapes this module's API returns and accepts.

import { z } from "zod";
import { FEED_KINDS, FEED_STATUSES } from "@/feeds/model/vocabulary.js";

export type FeedKind = (typeof FEED_KINDS)[number];

/** The source a feed's new entries are sent as. */
export const FEED_NOTIFICATIONS = "feeds";

type FeedStatus = (typeof FEED_STATUSES)[number];

// Poll-frequency choices, in minutes.
export const FEED_POLL_INTERVALS_MIN = [5, 15, 30, 60, 120, 360, 1440] as const;

// Auto-delete windows for entries, in days; 0 keeps every entry.
export const FEED_ENTRY_RETENTION_DAYS_OPTIONS = [1, 3, 7, 14, 30, 90, 0] as const;

export interface FeedCategory {
  id: number;
  name: string;
  unreadCount: number;
}

export interface Feed {
  id: number;
  categoryId: number | null;
  url: string;
  title: string;
  kind: FeedKind;
  lastFetchedAt: string | null;
  lastStatus: FeedStatus | null; // null = never synced yet
  lastError: string | null;
  unreadCount: number;
  // Unread entries created since this source was last opened (selected on the
  // Feeds page, expanded in the sidebar) — drives the freshness accent highlight.
  hasNew: boolean;
  // YouTube-only: when true, Shorts entries are skipped at fetch time.
  excludeShorts: boolean;
}

// One feed entry, joined with its feed's title/kind so a flattened list (the
// sidebar reader) doesn't need a separate feed lookup.
export interface FeedEntry {
  id: number;
  feedId: number;
  feedTitle: string;
  feedKind: FeedKind;
  title: string;
  link: string;
  publishedAt: string | null;
  summary: string | null; // stripped-HTML excerpt, best-effort
  contentHtml: string | null; // full article HTML, only when the feed itself provides it
  thumbnailUrl: string | null;
  read: boolean;
  createdAt: string;
  // Unread and created after the feed was last opened — the entry-level form
  // of `Feed.hasNew`, using the same comparison.
  isNew: boolean;
}

// Result of a "Validate" pre-check — never persisted.
export interface FeedValidateResult {
  ok: boolean;
  title?: string;
  kind?: FeedKind;
  sampleEntries?: { title: string; publishedAt: string | null }[];
  error?: string;
  // The real feed URL a YouTube/Twitch channel URL was converted to — present
  // only when the submitted URL wasn't already a literal feed URL. Subscribing
  // should use this instead of the originally-typed URL.
  resolvedUrl?: string;
}

// What's currently selected/filtered on the Feeds page — account-wide server
// state (not per-browser) so it survives a cleared client or a new device.
type FeedsLeftSelection =
  | { kind: "feed"; id: number }
  | { kind: "category"; id: number | null }
  | { kind: "all" };

export interface FeedsUiState {
  selection: FeedsLeftSelection;
  collapsed: (number | null)[];
  unreadOnly: boolean;
}

export const updateFeedPollIntervalSchema = z.object({
  // Only the discrete choices the UI offers (see FEED_POLL_INTERVALS_MIN).
  minutes: z.coerce
    .number()
    .int()
    .refine(
      (n) => (FEED_POLL_INTERVALS_MIN as readonly number[]).includes(n),
      "Unsupported poll interval",
    ),
});

export const updateFeedAutoReadDelaySchema = z.object({
  seconds: z.coerce.number().int().min(0).max(300),
});

export const updateFeedEntryRetentionDaysSchema = z.object({
  // 0 = never auto-delete, same "0 disables" convention as
  // feedAutoReadDelaySeconds/notificationRetentionDays.
  days: z.coerce
    .number()
    .int()
    .refine(
      (n) => (FEED_ENTRY_RETENTION_DAYS_OPTIONS as readonly number[]).includes(n),
      "Unsupported retention window",
    ),
});

const feedsLeftSelectionSchema = z.union([
  z.object({ kind: z.literal("feed"), id: z.number().int() }),
  z.object({ kind: z.literal("category"), id: z.number().int().nullable() }),
  z.object({ kind: z.literal("all") }),
]);

export const updateFeedsUiStateSchema = z.object({
  selection: feedsLeftSelectionSchema,
  collapsed: z.array(z.number().int().nullable()),
  unreadOnly: z.boolean(),
});

const feedUrl = z.string().trim().url().max(2000);

const feedName = z.string().trim().min(1).max(60);

export const createFeedCategorySchema = z.object({ name: feedName });

export const updateFeedCategorySchema = z.object({ name: feedName.optional() }).strict();

export type CreateFeedCategoryInput = z.infer<typeof createFeedCategorySchema>;

export type UpdateFeedCategoryInput = z.infer<typeof updateFeedCategorySchema>;

export const validateFeedSchema = z.object({ url: feedUrl });

export const createFeedSchema = z.object({
  url: feedUrl,
  title: z.string().trim().min(1).max(200).optional(),
  categoryId: z.coerce.number().int().positive().nullable().optional(),
  excludeShorts: z.boolean().optional(),
});

export type CreateFeedInput = z.infer<typeof createFeedSchema>;

export const updateFeedSchema = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    url: feedUrl.optional(),
    categoryId: z.coerce.number().int().positive().nullable().optional(),
    excludeShorts: z.boolean().optional(),
  })
  .strict();

export type UpdateFeedInput = z.infer<typeof updateFeedSchema>;

export const deleteFeedEntriesSchema = z.object({
  ids: z.array(z.number().int().positive()).min(1),
});

export const feedEntriesQuerySchema = z.object({
  categoryId: z.coerce.number().int().positive().optional(),
  // Comma-separated feed ids — scopes to a single feed (one id) or merges
  // entries across an arbitrary set of feeds (the Feeds page's "Uncategorized"
  // bucket, which has no real categoryId to filter on). Same convention as
  // quoteQuerySchema's `symbols`.
  feedIds: z.string().optional(),
  unreadOnly: z
    .enum(["true", "false"])
    .optional()
    .transform((v) => v === "true"),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export type FeedEntriesQuery = z.infer<typeof feedEntriesQuerySchema>;

export const updateFeedEntrySchema = z.object({ read: z.boolean() }).strict();

export const markAllFeedEntriesReadSchema = z.object({
  feedIds: z.array(z.number().int().positive()).optional(),
  categoryIds: z.array(z.number().int().positive()).optional(),
});

export const markFeedsViewedSchema = z.object({
  feedIds: z.array(z.number().int().positive()).min(1),
});
